import { Injectable } from '@angular/core';

import { BetSlip } from '@bpos/v1/my-bets';
import { CrmConfig, EarlyPayoutConfig, MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { UserService } from '@frontend/sports/user/feature';
import { differenceBy } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { MyBetsEarlyPayoutService } from '../../my-bets-base/early-payout/my-bets-earlypayout.service';
import { MyBetsEarlyPayoutServiceMyBetsFactory } from '../../my-bets-base/early-payout/my-bets-earlypayout.service.factory';
import { IBetSlipRepositoryResult, MigrationTabType, MyBetsBetSlip } from '../../my-bets-base/models/my-bets-viewmodels';
import { IMyBetsListRequest } from '../../my-bets/services/my-bets-api.service';
import { MyBetsFilterConstraint, MyBetsMigrationFilterContstraint } from '../models/my-bets-filter-constraints';
import { MyBetsLoaderService } from './my-bets-loader.service';
import { MyBetsPinningService } from './my-bets-pinning.service';
import { SettledBalanceService } from './settled-balance.service';

@Injectable()
export class MyBetsBetslipRepository {
    private isLoading = false;
    private myBetsEarlyPayoutService: MyBetsEarlyPayoutService;
    private readonly logger: SportsRemoteLogger;

    constructor(
        private myBetsLoader: MyBetsLoaderService,
        private mybetsConfig: MyBetsConfig,
        private epConfig: EarlyPayoutConfig,
        earlyPayoutServiceFactory: MyBetsEarlyPayoutServiceMyBetsFactory,
        private myBetsPinningService: MyBetsPinningService,
        private settledBalanceService: SettledBalanceService,
        private userService: UserService,
        loggerFactory: LoggerFactory,
        private sitecoreMyBets: SitecoreMyBetsConfig,
        private tokensConfig: CrmConfig,
    ) {
        this.myBetsEarlyPayoutService = earlyPayoutServiceFactory.create();
        this.logger = loggerFactory.getLogger('MyBetsBetslipRepository');
    }

    getSingleBetslip(betslipId: string, associatedAccount: string): Promise<IBetSlipRepositoryResult> {
        this.isLoading = true;

        return this.loadBwinBet(betslipId, associatedAccount)
            .then((result) => {
                return result;
            })
            .catch((e: Error) => {
                this.logger.error(e, 'Error occurred while loading a bet via the getSingleBetslip method');

                return {
                    betslips: [],
                    hasMore: false,
                    pageIndex: 1,
                    listType: BetslipListType.Settled,
                    errorLoadingBets: true,
                    associatedAccount: null,
                };
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    getAllBetslips(requestData: { filterConstraint?: MyBetsFilterConstraint }): Promise<IBetSlipRepositoryResult> {
        this.isLoading = true;

        return this.loadAllBwinBets({
            filterConstraint: requestData.filterConstraint || new MyBetsFilterConstraint(),
            eventIds: [],
        })
            .then((result) => {
                return result;
            })
            .catch((e: Error) => {
                this.logger.error(e, 'Error occurred while loading a bet via the getAllBetslips method');

                return {
                    betslips: [],
                    hasMore: false,
                    pageIndex: 1,
                    listType: BetslipListType.Settled,
                    errorLoadingBets: true,
                    associatedAccount: null,
                };
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    getBetSlips(requestData: {
        pageSize: number;
        eventIds?: string[];
        filterConstraint?: MyBetsFilterConstraint;
        listType?: BetslipListType;
    }): Promise<IBetSlipRepositoryResult> {
        this.isLoading = true;

        return this.loadBwinBets({
            pageSize: requestData.pageSize,
            pageIndex: 1,
            loadedBetslips: [],
            listType: requestData.listType === undefined ? null : requestData.listType,
            eventIds: requestData.eventIds || [],
            filterConstraint: requestData.filterConstraint || new MyBetsFilterConstraint(),
        })
            .then((result) => {
                return result;
            })
            .catch((e: Error) => {
                this.logger.error(e, 'Error occurred while loading bets via the getBetSlips method');

                return {
                    betslips: [],
                    hasMore: false,
                    pageIndex: 1,
                    listType: BetslipListType.Settled,
                    errorLoadingBets: true,
                    associatedAccount: null,
                };
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    getNextBetslips(requestData: {
        currentData: IBetSlipRepositoryResult;
        pageSize: number;
        eventIds?: string[];
        filterConstraint?: MyBetsFilterConstraint;
    }): Promise<IBetSlipRepositoryResult> {
        if (this.isLoading || !requestData.currentData.hasMore) {
            return Promise.reject();
        }

        this.isLoading = true;

        return this.loadBwinBets({
            loadedBetslips: requestData.currentData.betslips,
            eventIds: requestData.eventIds || [],
            pageIndex: requestData.currentData.pageIndex,
            pageSize: requestData.pageSize,
            filterConstraint: requestData.filterConstraint || new MyBetsFilterConstraint(),
            listType: requestData.currentData.listType,
        })
            .then((result) => {
                return result;
            })
            .catch((e: Error) => {
                this.logger.error(e, 'Error occurred while loading bets via the getNextBetslips method');

                return requestData.currentData;
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    moveBetToTop(betslipId: string, betslipList: MyBetsBetSlip[]): void {
        const index = betslipList.findIndex((betslip) => betslip.betslipId === betslipId);
        if (index > 0) {
            const moved = betslipList[index];
            betslipList.splice(index, 1);
            betslipList.unshift(moved);
        }
    }

    private async loadBwinBet(betslipId: string, associatedAccount: string): Promise<IBetSlipRepositoryResult> {
        const result = await firstValueFrom(this.myBetsLoader.getSingleBetslip(betslipId, associatedAccount));
        if (!result.betslip) {
            throw new Error(`Expected single betslip response for ${betslipId}`);
        }

        const betslip = MyBetsBetSlip.createFromBetslipResponse(result.betslip, result.events, this.tokensConfig);

        return {
            betslips: [betslip].filter((b) => b.bets.length > 0),
            hasMore: false,
            pageIndex: 1,
            listType: BetslipListType.Settled,
            errorLoadingBets: false,
        };
    }

    private async loadBwinBets(options: {
        loadedBetslips: MyBetsBetSlip[];
        pageSize: number;
        pageIndex: number;
        eventIds: string[];
        filterConstraint: MyBetsFilterConstraint;
        listType: BetslipListType | null;
    }): Promise<IBetSlipRepositoryResult> {
        let opts: IMyBetsListRequest = {
            index: options.pageIndex,
            maxItems: options.pageSize,
            typeFilter: options.listType,
            pinnedBetslipIds: this.resolvePinnedBetIds(options.listType, this.containsMigrationConstraint(options.filterConstraint)),
            eventIds: options.eventIds,
        };
        opts = options.filterConstraint.apply(opts);
        let listType = options.listType;

        const result = await firstValueFrom(this.myBetsLoader.getMyBets(opts));

        if (listType === null) {
            listType = result.typeFilter;
        }
        if (listType != null && listType === BetslipListType.Open) {
            const openBetCount = result.summary.openBetsCount;
            this.myBetsLoader.trackOpenBetsDetails(openBetCount);
        }
        const list = result.betslips
            .map((betslip: BetSlip) => {
                return MyBetsBetSlip.createFromBetslipResponse(betslip, result.events, this.tokensConfig);
            })
            .filter((b: MyBetsBetSlip) => b.bets.length > 0);

        this.settledBalanceService.processResult(opts, listType, list);

        const newBetslips = differenceBy(list, options.loadedBetslips, (b: { betslipId: any }) => b.betslipId);
        if (
            (this.epConfig.isEarlyPayoutEnabled || this.mybetsConfig.isEditMyBetEnabled) &&
            this.userService.isEarlyPayoutAllowed &&
            listType !== BetslipListType.Settled &&
            !opts.associatedAccount
        ) {
            if (options.pageIndex === 1) {
                // clear existing subscriptions just in case they were not cleared already
                this.myBetsEarlyPayoutService.dropEarlyPayoutSubscription();
            }

            await this.myBetsEarlyPayoutService.mapEarlyPayoutAmount(newBetslips);
        }

        return <IBetSlipRepositoryResult>{
            betslips: options.loadedBetslips.concat(newBetslips),
            // HACK: BPOSv4 will implement flag hasMore; this flag should be used here
            hasMore: newBetslips.length >= options.pageSize,
            pageIndex: options.pageIndex + 1,
            listType,
            errorLoadingBets: result.errorLoadingBets,
        };
    }

    private async loadAllBwinBets(options: { eventIds: string[]; filterConstraint: MyBetsFilterConstraint }): Promise<IBetSlipRepositoryResult> {
        let opts: IMyBetsListRequest = {
            index: 1,
            typeFilter: BetslipListType.Settled,
            eventIds: options.eventIds,
        };

        opts = options.filterConstraint.apply(opts);
        this.isLoading = true;

        const result = await firstValueFrom(this.myBetsLoader.getAllSettledMyBets(opts));

        const list = result.betslips
            .map((betslip: BetSlip) => {
                return MyBetsBetSlip.createFromBetslipResponse(betslip, result.events, this.tokensConfig);
            })
            .filter((b: MyBetsBetSlip) => b.bets.length > 0);

        return <IBetSlipRepositoryResult>{
            betslips: list,
            hasMore: false,
            pageIndex: 1,
            listType: BetslipListType.Settled,
            errorLoadingBets: result.errorLoadingBets,
        };
    }

    private resolvePinnedBetIds(listType: BetslipListType | null, isMigratedList: boolean | false): string | undefined {
        return listType !== BetslipListType.Settled ? this.myBetsPinningService.getPinnedBetIds(isMigratedList).join(',') : void 0;
    }

    private containsMigrationConstraint(filterConstraints: MyBetsFilterConstraint): boolean {
        const constraints = filterConstraints.constraints;

        return constraints.some((constraint) => constraint instanceof MyBetsMigrationFilterContstraint);
    }

    getBeforeFilter(
        associatedAccount: string,
        isDateRangeFilterAvailable: boolean = false,
        isSettledTab: boolean = false,
    ): MyBetsMigrationFilterContstraint {
        const toDate = new Date(this.sitecoreMyBets.myBets.MigrationDate);

        if (!isDateRangeFilterAvailable && isSettledTab) {
            const fromDate = new Date(toDate);
            fromDate.setDate(fromDate.getDate() - this.mybetsConfig.defaultDaysBeforeMigrationDate);

            return new MyBetsMigrationFilterContstraint(MigrationTabType.Before, toDate, associatedAccount, fromDate);
        }

        return new MyBetsMigrationFilterContstraint(MigrationTabType.Before, toDate, associatedAccount);
    }
}
